import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Shield, 
  Users as UsersIcon, 
  FileText, 
  Settings, 
  CheckCircle2,
  XCircle,
  Info,
  DollarSign,
  TrendingUp,
  Briefcase,
  HandshakeIcon,
  Users,
  Eye,
  Edit,
  UserCog,
  Download,
  Calendar,
  ChevronLeft,
  ChevronRight,
  History
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { type UserPermissions, type PermissionViewLevel, type PermissionEditLevel, DEFAULT_PERMISSIONS, ROLE_DESCRIPTIONS } from '@/types/user';
import { useUsersList } from '@/hooks/useUsersData';
import { UserAvatar } from '@/components/user-avatar';
import { format } from 'date-fns';

interface RoleData {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  status: 'active' | 'inactive';
  permissions: UserPermissions;
  userCount: number;
}

// Mock data for system roles
const SYSTEM_ROLES: Record<string, RoleData> = {
  administrator: {
    id: 'administrator',
    name: 'Administrator',
    description: ROLE_DESCRIPTIONS.administrator,
    isSystem: true,
    status: 'active',
    permissions: DEFAULT_PERMISSIONS.administrator,
    userCount: 0,
  },
  user: {
    id: 'user',
    name: 'User',
    description: ROLE_DESCRIPTIONS.user,
    isSystem: true,
    status: 'active',
    permissions: DEFAULT_PERMISSIONS.user,
    userCount: 0,
  },
  view_only: {
    id: 'view_only',
    name: 'View Only',
    description: ROLE_DESCRIPTIONS.view_only,
    isSystem: true,
    status: 'active',
    permissions: DEFAULT_PERMISSIONS.view_only,
    userCount: 0,
  },
  collections_manager: {
    id: 'collections_manager',
    name: 'Collections Manager',
    description: 'Custom role for managing collections and customer communications',
    isSystem: false,
    status: 'active',
    permissions: {
      manageUsers: false,
      manageCompanies: false,
      manageIntegrations: false,
      configureAutomation: false,
      configureAI: false,
      viewReports: true,
      manageAccounts: true,
      sendCommunications: true,
      logActivities: true,
      viewPayments: true,
      allocatePayments: true,
      reverseAllocations: false,
      customerAccountsView: 'assigned_default',
      customerAccountsEdit: 'assigned',
      customerAccountsReassign: false,
      collectionCasesView: 'assigned_default',
      collectionCasesCreate: true,
      collectionCasesEdit: 'assigned',
      collectionCasesDelete: false,
      collectionCasesReassign: false,
      promiseToPayView: 'assigned_default',
      promiseToPayCreate: true,
      promiseToPayEdit: 'assigned',
      promiseToPayDelete: false,
      promiseToPayReassign: false,
      viewCreditControlDashboard: false,
      viewCollectionsDashboard: false,
      viewDisputesDashboard: false,
      viewAgeingAnalysis: false,
      viewMultiCompanyDashboard: false,
      accessManagerViews: false,
      adminManageUsersView: false,
      adminManageUsersCreate: false,
      adminManageUsersEdit: false,
      adminManageUsersDelete: false,
      adminManageCompaniesView: false,
      adminManageCompaniesCreate: false,
      adminManageCompaniesEdit: false,
      adminManageCompaniesDelete: false,
      adminManageGeneralSettingsView: false,
      adminManageGeneralSettingsCreate: false,
      adminManageGeneralSettingsEdit: false,
      adminManageGeneralSettingsDelete: false,
      adminManageIntegrationsView: false,
      adminManageIntegrationsCreate: false,
      adminManageIntegrationsEdit: false,
      adminManageIntegrationsDelete: false,
      adminManageTemplatesView: false,
      adminManageTemplatesCreate: false,
      adminManageTemplatesEdit: false,
      adminManageTemplatesDelete: false,
      adminManageCollectionStrategiesView: false,
      adminManageCollectionStrategiesCreate: false,
      adminManageCollectionStrategiesEdit: false,
      adminManageCollectionStrategiesDelete: false,
      adminManageDisputeTypesView: false,
      adminManageDisputeTypesCreate: false,
      adminManageDisputeTypesEdit: false,
      adminManageDisputeTypesDelete: false,
      adminConfigureAIView: false,
      adminConfigureAICreate: false,
      adminConfigureAIEdit: false,
      adminConfigureAIDelete: false,
    },
    userCount: 0,
  },
};

// Mock data for users assigned to roles
interface AssignedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'active' | 'inactive';
  dateAssigned: string;
  avatar?: string;
}

const ASSIGNED_USERS_BY_ROLE: Record<string, AssignedUser[]> = {
  administrator: [
    {
      id: '1',
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@company.com',
      status: 'active',
      dateAssigned: '2026-01-15',
    },
    {
      id: '2',
      firstName: 'David',
      lastName: 'Williams',
      email: 'david.williams@company.com',
      status: 'active',
      dateAssigned: '2026-01-22',
    },
  ],
  user: [
    {
      id: '3',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@company.com',
      status: 'active',
      dateAssigned: '2026-01-10',
    },
    {
      id: '4',
      firstName: 'Michael',
      lastName: 'Brown',
      email: 'michael.brown@company.com',
      status: 'active',
      dateAssigned: '2026-01-18',
    },
    {
      id: '5',
      firstName: 'Emily',
      lastName: 'Davis',
      email: 'emily.davis@company.com',
      status: 'inactive',
      dateAssigned: '2026-01-05',
    },
  ],
  view_only: [
    {
      id: '6',
      firstName: 'Robert',
      lastName: 'Wilson',
      email: 'robert.wilson@company.com',
      status: 'active',
      dateAssigned: '2026-01-20',
    },
  ],
  collections_manager: [],
};

// Audit log types
type AuditActionType = 
  | 'role_created' 
  | 'role_duplicated' 
  | 'permission_added' 
  | 'permission_removed' 
  | 'role_details_updated';

interface AuditLogEntry {
  id: string;
  dateTime: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  actionType: AuditActionType;
  details: string;
}

// Mock audit log data for Administrator role
const AUDIT_LOG_DATA: Record<string, AuditLogEntry[]> = {
  administrator: [
    {
      id: '1',
      dateTime: '2026-02-05T14:32:00',
      userId: '2',
      userName: 'Jane Smith',
      actionType: 'role_details_updated',
      details: 'Changed role description',
    },
    {
      id: '2',
      dateTime: '2026-02-03T09:15:00',
      userId: '1',
      userName: 'John Admin',
      actionType: 'permission_added',
      details: "Added 'View Reports' to Analytics & Reporting",
    },
    {
      id: '3',
      dateTime: '2026-01-28T16:45:00',
      userId: 'system',
      userName: 'System',
      actionType: 'role_created',
      details: 'Administrator role created during system setup',
    },
  ],
  user: [
    {
      id: '4',
      dateTime: '2026-02-10T11:20:00',
      userId: '1',
      userName: 'John Admin',
      actionType: 'permission_removed',
      details: "Removed 'Delete' permission for Disputes",
    },
    {
      id: '5',
      dateTime: '2026-02-08T15:30:00',
      userId: '2',
      userName: 'Jane Smith',
      actionType: 'role_details_updated',
      details: 'Changed status from Active to Inactive',
    },
    {
      id: '6',
      dateTime: '2026-02-01T10:00:00',
      userId: '1',
      userName: 'John Admin',
      actionType: 'permission_added',
      details: "Added 'Edit All' permission for Customer Accounts",
    },
    {
      id: '7',
      dateTime: '2026-01-28T16:45:00',
      userId: 'system',
      userName: 'System',
      actionType: 'role_created',
      details: 'User role created during system setup',
    },
  ],
  view_only: [
    {
      id: '8',
      dateTime: '2026-01-28T16:45:00',
      userId: 'system',
      userName: 'System',
      actionType: 'role_created',
      details: 'View Only role created during system setup',
    },
  ],
  collections_manager: [
    {
      id: '9',
      dateTime: '2026-02-12T14:15:00',
      userId: '1',
      userName: 'John Admin',
      actionType: 'permission_added',
      details: "Added 'Reassign' permission for Collection Cases",
    },
    {
      id: '10',
      dateTime: '2026-02-10T09:30:00',
      userId: '2',
      userName: 'Jane Smith',
      actionType: 'role_details_updated',
      details: "Changed role name from 'Credit Controller' to 'Collections Manager'",
    },
    {
      id: '11',
      dateTime: '2026-02-05T16:00:00',
      userId: '1',
      userName: 'John Admin',
      actionType: 'role_duplicated',
      details: "Role duplicated from 'Manager' role",
    },
  ],
};

// Permission area descriptions for the summary
const PERMISSION_AREA_DESCRIPTIONS = {
  customerAccounts: (perms: UserPermissions) => {
    if (
      perms.customerAccountsView === 'all' &&
      perms.customerAccountsEdit === 'all' &&
      perms.customerAccountsReassign
    ) return 'Full access — view, edit and reassign all accounts';
    const parts: string[] = [];
    if (perms.customerAccountsView === 'all') parts.push('View all accounts');
    else if (perms.customerAccountsView === 'assigned_default') parts.push('View all (assigned by default)');
    else if (perms.customerAccountsView === 'assigned_only') parts.push('View assigned only');
    if (perms.customerAccountsEdit === 'all') parts.push('Edit all');
    else if (perms.customerAccountsEdit === 'assigned') parts.push('Edit assigned');
    if (perms.customerAccountsReassign) parts.push('Reassign accounts');
    if (!parts.length) return 'No permissions assigned';
    return parts.join(', ');
  },
  payments: (perms: UserPermissions) => {
    if (perms.viewPayments && perms.allocatePayments && perms.reverseAllocations)
      return 'Full access — view, allocate and reverse payments';
    const parts: string[] = [];
    if (perms.viewPayments) parts.push('View payments');
    if (perms.allocatePayments) parts.push('Allocate payments');
    if (perms.reverseAllocations) parts.push('Reverse allocations');
    if (!parts.length) return 'No permissions assigned';
    return parts.join(', ');
  },
  collections: (perms: UserPermissions) => {
    if (
      perms.collectionCasesView === 'all' &&
      perms.collectionCasesCreate &&
      perms.collectionCasesEdit === 'all' &&
      perms.collectionCasesDelete &&
      perms.promiseToPayView === 'all' &&
      perms.promiseToPayCreate &&
      perms.promiseToPayEdit === 'all' &&
      perms.promiseToPayDelete
    ) return 'Full access — all cases and promises to pay';
    const ccParts: string[] = [];
    if (perms.collectionCasesView === 'all') ccParts.push('View all cases');
    else if (perms.collectionCasesView === 'assigned_default') ccParts.push('View all cases (assigned by default)');
    else if (perms.collectionCasesView === 'assigned_only') ccParts.push('View assigned cases');
    if (perms.collectionCasesCreate) ccParts.push('Create');
    if (perms.collectionCasesEdit === 'all') ccParts.push('Edit all');
    else if (perms.collectionCasesEdit === 'assigned') ccParts.push('Edit assigned');
    if (perms.collectionCasesDelete) ccParts.push('Delete');
    if (perms.collectionCasesReassign) ccParts.push('Reassign');
    const ptpParts: string[] = [];
    if (perms.promiseToPayView === 'all') ptpParts.push('View all PTPs');
    else if (perms.promiseToPayView === 'assigned_default') ptpParts.push('View all PTPs (assigned by default)');
    else if (perms.promiseToPayView === 'assigned_only') ptpParts.push('View assigned PTPs');
    if (perms.promiseToPayCreate) ptpParts.push('Create PTPs');
    if (perms.promiseToPayEdit === 'all') ptpParts.push('Edit all PTPs');
    else if (perms.promiseToPayEdit === 'assigned') ptpParts.push('Edit assigned PTPs');
    if (perms.promiseToPayDelete) ptpParts.push('Delete PTPs');
    if (perms.promiseToPayReassign) ptpParts.push('Reassign PTPs');
    const parts: string[] = [];
    if (ccParts.length) parts.push(`Cases: ${ccParts.join(', ')}`);
    if (ptpParts.length) parts.push(`PTPs: ${ptpParts.join(', ')}`);
    if (!parts.length) return 'No permissions assigned';
    return parts.join(' | ');
  },
  disputeManagement: (perms: UserPermissions) => {
    if (perms.manageAccounts) return 'Full access — view and create disputes';
    return 'No permissions assigned';
  },
  creditScoring: (_perms: UserPermissions) => {
    return 'No permissions assigned';
  },
  analyticsReporting: (perms: UserPermissions) => {
    if (
      perms.viewCreditControlDashboard &&
      perms.viewCollectionsDashboard &&
      perms.viewDisputesDashboard &&
      perms.viewAgeingAnalysis &&
      perms.viewMultiCompanyDashboard &&
      perms.accessManagerViews
    ) return 'Full access to all dashboards and reports';
    const parts: string[] = [];
    if (perms.viewCreditControlDashboard) parts.push('Credit Control Dashboard');
    if (perms.viewCollectionsDashboard) parts.push('Collections Dashboard');
    if (perms.viewDisputesDashboard) parts.push('Disputes Dashboard');
    if (perms.viewAgeingAnalysis) parts.push('Ageing Analysis');
    if (perms.viewMultiCompanyDashboard) parts.push('Multi-Company Dashboard');
    if (perms.accessManagerViews) parts.push('Manager Views');
    if (!parts.length) return 'No permissions assigned';
    return parts.join(', ');
  },
  administration: (perms: UserPermissions) => {
    if (
      perms.adminManageUsersView &&
      perms.adminManageCompaniesView &&
      perms.adminManageGeneralSettingsView &&
      perms.adminManageIntegrationsView &&
      perms.adminManageTemplatesView &&
      perms.adminManageCollectionStrategiesView &&
      perms.adminManageDisputeTypesView &&
      perms.adminConfigureAIView
    ) return 'Full access to all settings and administration';
    const parts: string[] = [];
    if (perms.manageUsers) parts.push('Manage users');
    if (perms.manageCompanies) parts.push('Manage company settings');
    if (perms.manageIntegrations) parts.push('Manage integrations');
    if (perms.configureAutomation) parts.push('Configure automation');
    if (perms.configureAI) parts.push('Configure AI');
    if (!parts.length) return 'No permissions assigned';
    return parts.join(', ');
  },
};

const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

export function RoleDetailsPage() {
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const location = useLocation();
  const { data: users = [] } = useUsersList();

  // Get initial role data
  const initialRole = roleId ? SYSTEM_ROLES[roleId] : null;

  // Calculate user count
  const userCount = users.filter(user => user.role === roleId).length;

  // Form state
  const [formData, setFormData] = useState<RoleData>({
    id: initialRole?.id || '',
    name: initialRole?.name || '',
    description: initialRole?.description || '',
    isSystem: initialRole?.isSystem || false,
    status: initialRole?.status || 'active',
    permissions: initialRole?.permissions || DEFAULT_PERMISSIONS.view_only,
    userCount: userCount,
  });

  const [originalData, setOriginalData] = useState<RoleData>(formData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Audit tab state
  const [auditDateRange, setAuditDateRange] = useState<'7days' | '30days' | '90days' | 'all'>('all');
  const [auditUserFilter, setAuditUserFilter] = useState<string>('all');
  const [auditActionFilter, setAuditActionFilter] = useState<string>('all');
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const AUDIT_ITEMS_PER_PAGE = 25;

  // Update user count when users data changes
  useEffect(() => {
    const count = users.filter(user => user.role === roleId).length;
    setFormData(prev => ({ ...prev, userCount: count }));
    setOriginalData(prev => ({ ...prev, userCount: count }));
  }, [users, roleId]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalData]);

  // Handle browser back/forward
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Role name must be ${MAX_NAME_LENGTH} characters or less`;
    } else if (formData.name !== originalData.name) {
      // Check for duplicate names (in real app, this would check against all roles)
      const existingRoles = Object.values(SYSTEM_ROLES);
      if (existingRoles.some(role => role.name.toLowerCase() === formData.name.toLowerCase() && role.id !== formData.id)) {
        newErrors.name = 'A role with this name already exists';
      }
    }

    if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    // In a real app, this would save to the backend
    setOriginalData(formData);
    setHasUnsavedChanges(false);
    toast.success('Role saved successfully');
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation('cancel');
      setShowUnsavedDialog(true);
    } else {
      navigate('/settings/users?tab=roles');
    }
  };

  const handleNavigateAway = (path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  };

  const handleTabChange = (value: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(`tab:${value}`);
      setShowUnsavedDialog(true);
    } else {
      setActiveTab(value);
    }
  };

  const confirmNavigation = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedDialog(false);
    
    if (pendingNavigation === 'cancel') {
      navigate('/settings/users?tab=roles');
    } else if (pendingNavigation?.startsWith('tab:')) {
      const tabName = pendingNavigation.replace('tab:', '');
      setActiveTab(tabName);
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    
    setPendingNavigation(null);
  };

  const cancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  const handleFieldChange = (field: keyof RoleData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isFormValid = !formData.isSystem && formData.name.trim() && Object.keys(errors).length === 0;

  // Audit log helper functions
  const getActionTypeBadgeColor = (actionType: AuditActionType): string => {
    switch (actionType) {
      case 'role_created':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20';
      case 'role_duplicated':
        return 'bg-purple-500/10 text-purple-700 dark:text-purple-400 hover:bg-purple-500/20';
      case 'permission_added':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20';
      case 'permission_removed':
        return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 hover:bg-orange-500/20';
      case 'role_details_updated':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 hover:bg-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  const getActionTypeLabel = (actionType: AuditActionType): string => {
    switch (actionType) {
      case 'role_created':
        return 'Role Created';
      case 'role_duplicated':
        return 'Role Duplicated';
      case 'permission_added':
        return 'Permission Added';
      case 'permission_removed':
        return 'Permission Removed';
      case 'role_details_updated':
        return 'Role Details Updated';
      default:
        return actionType;
    }
  };

  const filterAuditLogs = (logs: AuditLogEntry[]): AuditLogEntry[] => {
    let filtered = [...logs];

    // Filter by date range
    if (auditDateRange !== 'all') {
      const now = new Date();
      const daysMap = { '7days': 7, '30days': 30, '90days': 90 };
      const days = daysMap[auditDateRange];
      const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      
      filtered = filtered.filter(log => new Date(log.dateTime) >= cutoffDate);
    }

    // Filter by user
    if (auditUserFilter !== 'all') {
      filtered = filtered.filter(log => log.userId === auditUserFilter);
    }

    // Filter by action type
    if (auditActionFilter !== 'all') {
      filtered = filtered.filter(log => log.actionType === auditActionFilter);
    }

    return filtered;
  };

  const getUniqueAuditUsers = (logs: AuditLogEntry[]): Array<{ id: string; name: string }> => {
    const uniqueUsers = new Map<string, string>();
    logs.forEach(log => {
      if (!uniqueUsers.has(log.userId)) {
        uniqueUsers.set(log.userId, log.userName);
      }
    });
    return Array.from(uniqueUsers.entries()).map(([id, name]) => ({ id, name }));
  };

  const handleExportAuditLog = () => {
    const logs = AUDIT_LOG_DATA[roleId || ''] || [];
    const filtered = filterAuditLogs(logs);
    
    // Create CSV content
    const headers = ['Date/Time', 'User', 'Action Type', 'Details'];
    const rows = filtered.map(log => [
      format(new Date(log.dateTime), 'dd MMM yyyy, HH:mm'),
      log.userName,
      getActionTypeLabel(log.actionType),
      log.details,
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.name}_audit_log_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Audit log exported successfully');
  };

  // Get audit logs for current role
  const auditLogs = AUDIT_LOG_DATA[roleId || ''] || [];
  const filteredAuditLogs = filterAuditLogs(auditLogs);
  const totalAuditPages = Math.ceil(filteredAuditLogs.length / AUDIT_ITEMS_PER_PAGE);
  const paginatedAuditLogs = filteredAuditLogs.slice(
    (auditCurrentPage - 1) * AUDIT_ITEMS_PER_PAGE,
    auditCurrentPage * AUDIT_ITEMS_PER_PAGE
  );
  const uniqueAuditUsers = getUniqueAuditUsers(auditLogs);

  if (!initialRole) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Role not found</h2>
          <p className="text-muted-foreground mb-4">The role you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/settings/users?tab=roles')}>
            Back to Roles & Permissions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="px-6 py-4">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">{formData.name}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={!isFormValid || !hasUnsavedChanges}
                className="bg-primary hover:bg-primary/90"
              >
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="px-6">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList>
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="customer-accounts">
                Customer Accounts
              </TabsTrigger>
              <TabsTrigger value="payments">
                Payments
              </TabsTrigger>
              <TabsTrigger value="collections">
                Collections
              </TabsTrigger>
              <TabsTrigger value="dispute-management">
                Dispute Management
              </TabsTrigger>
              <TabsTrigger value="credit-scoring">
                Credit Scoring
              </TabsTrigger>
              <TabsTrigger value="analytics-reporting">
                Analytics & Reporting
              </TabsTrigger>
              <TabsTrigger value="administration">
                Administration
              </TabsTrigger>
              <TabsTrigger value="users">
                Users
              </TabsTrigger>
              <TabsTrigger value="audit">
                Audit
              </TabsTrigger>
            </TabsList>

          {/* Tab Content */}
          <div className="p-6 bg-muted/20">
            <TabsContent value="overview" className="mt-0">
              <div className="max-w-6xl mx-auto space-y-6">
                {/* Top Row - Role Information Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Role Details Card */}
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle>Role Information</CardTitle>
                          <CardDescription>Basic details and configuration</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Role Name */}
                      <div className="space-y-2">
                        <Label htmlFor="role-name" className="text-sm font-medium">
                          Role Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="role-name"
                          value={formData.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                          disabled={formData.isSystem}
                          maxLength={MAX_NAME_LENGTH}
                          className={errors.name ? 'border-destructive' : ''}
                        />
                        <div className="flex items-center justify-between text-xs">
                          {errors.name ? (
                            <span className="text-destructive">{errors.name}</span>
                          ) : (
                            <span className="text-muted-foreground">
                              {formData.isSystem ? 'System roles cannot be renamed' : 'Must be unique'}
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {formData.name.length}/{MAX_NAME_LENGTH}
                          </span>
                        </div>
                      </div>

                      {/* Role Description */}
                      <div className="space-y-2">
                        <Label htmlFor="role-description" className="text-sm font-medium">
                          Role Description
                        </Label>
                        <Textarea
                          id="role-description"
                          value={formData.description}
                          onChange={(e) => handleFieldChange('description', e.target.value)}
                          disabled={formData.isSystem}
                          placeholder="Describe the purpose and responsibilities of this role..."
                          rows={4}
                          maxLength={MAX_DESCRIPTION_LENGTH}
                          className={errors.description ? 'border-destructive' : ''}
                        />
                        <div className="flex items-center justify-between text-xs">
                          {errors.description ? (
                            <span className="text-destructive">{errors.description}</span>
                          ) : (
                            <span className="text-muted-foreground">
                              {formData.isSystem ? 'System role description cannot be changed' : 'Optional'}
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Role Settings Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Settings className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle>Settings</CardTitle>
                          <CardDescription>Role configuration</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Role Type */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Role Type</Label>
                        <div>
                          <Badge variant={formData.isSystem ? 'secondary' : 'default'} className="text-sm">
                            {formData.isSystem ? 'Platform Role' : 'Custom Role'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formData.isSystem 
                            ? 'Platform roles are predefined and cannot be deleted' 
                            : 'Custom roles can be modified and deleted'}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="space-y-2">
                        <Label htmlFor="role-status" className="text-sm font-medium">
                          Status
                        </Label>
                        <div className="flex items-center gap-3">
                          <Switch
                            id="role-status"
                            checked={formData.status === 'active'}
                            onCheckedChange={(checked) => 
                              handleFieldChange('status', checked ? 'active' : 'inactive')
                            }
                            disabled={formData.isSystem}
                          />
                          <span className={`text-sm font-medium ${
                            formData.status === 'active' ? 'text-green-600' : 'text-muted-foreground'
                          }`}>
                            {formData.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formData.isSystem 
                            ? 'System roles are always active' 
                            : 'Inactive roles cannot be assigned to users'}
                        </p>
                      </div>

                      {/* Users Assigned */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Users Assigned</Label>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <UsersIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold">
                              {formData.userCount}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {formData.userCount === 1 ? 'user' : 'users'} assigned
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Permissions Summary Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Shield className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle>Permissions Summary</CardTitle>
                        <CardDescription>Overview of all permissions assigned to this role</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Customer Accounts */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.customerAccounts(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Customer Accounts</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.customerAccounts(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.customerAccounts(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Payments */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.payments(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Payments</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.payments(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.payments(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Collections */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.collections(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Collections</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.collections(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.collections(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Dispute Management */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.disputeManagement(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Dispute Management</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.disputeManagement(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.disputeManagement(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Credit Scoring */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.creditScoring(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Credit Scoring</div>
                          <p className="text-xs text-muted-foreground">
                            {PERMISSION_AREA_DESCRIPTIONS.creditScoring(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Analytics & Reporting */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.analyticsReporting(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Analytics & Reporting</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.analyticsReporting(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.analyticsReporting(formData.permissions)}
                          </p>
                        </div>
                      </div>

                      {/* Administration */}
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="mt-0.5">
                          {PERMISSION_AREA_DESCRIPTIONS.administration(formData.permissions) === 'No permissions assigned' ? (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1">Administration</div>
                          <p className={`text-xs ${
                            PERMISSION_AREA_DESCRIPTIONS.administration(formData.permissions) === 'No permissions assigned'
                              ? 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>
                            {PERMISSION_AREA_DESCRIPTIONS.administration(formData.permissions)}
                          </p>
                        </div>
                      </div>
                    </div>


                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Customer Accounts Permissions Tab */}
            <TabsContent value="customer-accounts" className="mt-0">
              <div className="max-w-4xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <CardTitle>Customer Accounts Permissions</CardTitle>
                        <CardDescription>
                          Configure access and editing permissions for customer accounts
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    {/* View Permission Section */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-500/10 rounded">
                          <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <Label className="text-base font-semibold">View Access</Label>
                      </div>
                      <div className="pl-8 space-y-3">
                        <RadioGroup
                          value={formData.permissions.customerAccountsView}
                          onValueChange={(value: string) => {
                                                        const v = value as PermissionViewLevel;
                                                        setFormData(prev => ({
                                                          ...prev,
                                                          permissions: {
                                                            ...prev.permissions,
                                                            customerAccountsView: v,
                                // If setting to 'none', also clear edit permissions
                                customerAccountsEdit: v === 'none' ? 'none' : prev.permissions.customerAccountsEdit,
                              }
                            }));
                          }}
                          disabled={formData.isSystem}
                          className="space-y-3"
                        >
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="all" id="view-all" />
                            <Label htmlFor="view-all" className="font-medium cursor-pointer">
                              View All Customer Accounts
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="assigned_default" id="view-assigned-default" />
                            <Label htmlFor="view-assigned-default" className="font-medium cursor-pointer">
                              View Assigned by Default
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="assigned_only" id="view-assigned-only" />
                            <Label htmlFor="view-assigned-only" className="font-medium cursor-pointer">
                              View Assigned Only
                            </Label>
                          </div>
                        </RadioGroup>
                        <p className="text-sm text-muted-foreground">
                          Controls which customer accounts users with this role can access
                        </p>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Edit Permission Section */}
                    <div className={`space-y-4 transition-opacity ${
                      formData.permissions.customerAccountsView === 'none' ? 'opacity-50' : ''
                    }`}>
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-amber-500/10 rounded">
                          <Edit className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <Label className="text-base font-semibold">Edit Access</Label>
                      </div>
                      <div className="pl-8 space-y-3">
                        <RadioGroup
                          value={formData.permissions.customerAccountsEdit}
                          onValueChange={(value: string) => {
                                                        const v = value as PermissionEditLevel;
                                                        setFormData(prev => ({
                                                          ...prev,
                                                          permissions: {
                                                            ...prev.permissions,
                                                            customerAccountsEdit: v,
                                // If enabling edit and view is 'none', set view to 'assigned_default'
                                customerAccountsView: 
                                  v !== 'none' && prev.permissions.customerAccountsView === 'none'
                                    ? 'assigned_default'
                                    : prev.permissions.customerAccountsView,
                              }
                            }));
                          }}
                          disabled={formData.isSystem || formData.permissions.customerAccountsView === 'none'}
                          className="space-y-3"
                        >
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="all" id="edit-all" />
                            <Label htmlFor="edit-all" className="font-medium cursor-pointer">
                              Edit All Customer Accounts
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="assigned" id="edit-assigned" />
                            <Label htmlFor="edit-assigned" className="font-medium cursor-pointer">
                              Edit Assigned Only
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="none" id="edit-none" />
                            <Label htmlFor="edit-none" className="font-medium cursor-pointer">
                              No Edit Access
                            </Label>
                          </div>
                        </RadioGroup>
                        <p className="text-sm text-muted-foreground">
                          Controls which customer accounts users can modify
                        </p>
                        {formData.permissions.customerAccountsView === 'none' && (
                          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                              Edit permission requires at least one View option to be selected
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Reassign User Permission Section */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-500/10 rounded">
                          <UserCog className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <Label className="text-base font-semibold">Reassignment Access</Label>
                      </div>
                      <div className="pl-8 space-y-3">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="reassign-user"
                            checked={formData.permissions.customerAccountsReassign}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  customerAccountsReassign: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="space-y-1">
                            <Label
                              htmlFor="reassign-user"
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              Allow users to reassign customer accounts to other users
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Enables reassignment of customer account ownership
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info banner for system roles */}
                    {formData.isSystem && (
                      <div className="mt-6 pt-6 border-t">
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <p>
                            This is a system role. Permissions cannot be modified directly. 
                            Create a custom role to define specific permissions.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="payments" className="mt-0">
              <div className="max-w-4xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <DollarSign className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle>Payments Permissions</CardTitle>
                        <CardDescription>
                          Configure payment-related permissions for this role
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* View Payments Permission */}
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id="view-payments"
                        checked={formData.permissions.viewPayments}
                        onCheckedChange={(checked) => {
                          // When unchecking View Payments, also uncheck dependent permissions
                          if (!checked) {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              viewPayments: false,
                              allocatePayments: false,
                              reverseAllocations: false,
                            });
                          } else {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              viewPayments: true,
                            });
                          }
                        }}
                        disabled={formData.isSystem}
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label 
                          htmlFor="view-payments" 
                          className="text-sm font-semibold cursor-pointer"
                        >
                          View Payments
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Can view payment records and transaction history
                        </p>
                      </div>
                    </div>

                    {/* Allocate Payments Permission */}
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id="allocate-payments"
                        checked={formData.permissions.allocatePayments}
                        onCheckedChange={(checked) => {
                          // When checking Allocate Payments, also check View Payments
                          if (checked) {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              viewPayments: true,
                              allocatePayments: true,
                            });
                          } else {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              allocatePayments: false,
                            });
                          }
                        }}
                        disabled={formData.isSystem}
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label 
                          htmlFor="allocate-payments" 
                          className="text-sm font-semibold cursor-pointer"
                        >
                          Allocate Payments
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Can allocate payments to invoices and customer accounts
                        </p>
                      </div>
                    </div>

                    {/* Reverse Allocations Permission */}
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id="reverse-allocations"
                        checked={formData.permissions.reverseAllocations}
                        onCheckedChange={(checked) => {
                          // When checking Reverse Allocations, also check View Payments
                          if (checked) {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              viewPayments: true,
                              reverseAllocations: true,
                            });
                          } else {
                            handleFieldChange('permissions', {
                              ...formData.permissions,
                              reverseAllocations: false,
                            });
                          }
                        }}
                        disabled={formData.isSystem}
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label 
                          htmlFor="reverse-allocations" 
                          className="text-sm font-semibold cursor-pointer"
                        >
                          Reverse Allocations
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Can reverse previously allocated payments
                        </p>
                      </div>
                    </div>

                    {/* Info banner for system roles */}
                    {formData.isSystem && (
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <p>
                            This is a system role. Permissions cannot be modified directly. 
                            Create a custom role to define specific permissions.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="collections" className="mt-0">
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Collection Cases Column */}
                      <div className="space-y-6">
                        <div className="flex items-center gap-3 pb-4 border-b">
                          <div className="p-2 bg-blue-500/10 rounded-lg">
                            <Briefcase className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">Collection Cases</h3>
                            <p className="text-sm text-muted-foreground">Manage collection case permissions</p>
                          </div>
                        </div>
                        
                        <div className="space-y-6">
                    {/* View Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">View</Label>
                      </div>
                      <RadioGroup
                        value={formData.permissions.collectionCasesView}
                        onValueChange={(value: string) => {
                          if (formData.isSystem) return;
                          const v = value as PermissionViewLevel;
                          const newPermissions = { ...formData.permissions, collectionCasesView: v };
                          
                          // If setting to 'none', clear all dependent permissions
                          if (value === 'none') {
                            newPermissions.collectionCasesCreate = false;
                            newPermissions.collectionCasesEdit = 'none';
                            newPermissions.collectionCasesDelete = false;
                            newPermissions.collectionCasesReassign = false;
                          }
                          
                          setFormData(prev => ({ ...prev, permissions: newPermissions }));
                        }}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="all" id="cc-view-all" disabled={formData.isSystem} />
                          <Label htmlFor="cc-view-all" className="font-normal cursor-pointer">
                            View All
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned_default" id="cc-view-assigned-default" disabled={formData.isSystem} />
                          <Label htmlFor="cc-view-assigned-default" className="font-normal cursor-pointer">
                            View Assigned by default
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned_only" id="cc-view-assigned-only" disabled={formData.isSystem} />
                          <Label htmlFor="cc-view-assigned-only" className="font-normal cursor-pointer">
                            View Assigned Only
                          </Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-muted-foreground">
                        Controls which collection cases users can access
                      </p>
                    </div>

                    {/* Create Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Create</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="cc-create"
                          checked={formData.permissions.collectionCasesCreate}
                          onCheckedChange={(checked) => {
                            const newPermissions = { ...formData.permissions, collectionCasesCreate: checked === true };
                            
                            // When Create is checked, automatically select "View Assigned by default" if no view is selected
                            if (checked && newPermissions.collectionCasesView === 'none') {
                              newPermissions.collectionCasesView = 'assigned_default';
                            }
                            
                            setFormData(prev => ({ ...prev, permissions: newPermissions }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="cc-create" className="text-sm font-medium leading-none cursor-pointer">
                            Create collection cases
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can create new collection cases
                      </p>
                    </div>

                    {/* Edit Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Edit</Label>
                      </div>
                      <RadioGroup
                        value={formData.permissions.collectionCasesEdit}
                        onValueChange={(value: string) => {
                          if (formData.isSystem) return;
                          const v = value as PermissionEditLevel;
                          const newPermissions = { ...formData.permissions, collectionCasesEdit: v };
                          
                          // When Edit is selected, automatically enable a View option if none selected
                          if (value !== 'none' && newPermissions.collectionCasesView === 'none') {
                            newPermissions.collectionCasesView = 'assigned_default';
                          }
                          
                          setFormData(prev => ({ ...prev, permissions: newPermissions }));
                        }}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="all" id="cc-edit-all" disabled={formData.isSystem} />
                          <Label htmlFor="cc-edit-all" className="font-normal cursor-pointer">
                            Edit All
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned" id="cc-edit-assigned" disabled={formData.isSystem} />
                          <Label htmlFor="cc-edit-assigned" className="font-normal cursor-pointer">
                            Edit Assigned
                          </Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-muted-foreground">
                        Controls which collection cases users can modify
                      </p>
                    </div>

                    {/* Delete Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Delete</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="cc-delete"
                          checked={formData.permissions.collectionCasesDelete}
                          onCheckedChange={(checked) => {
                            const newPermissions = { ...formData.permissions, collectionCasesDelete: checked === true };
                            
                            // When Delete is checked, automatically enable a View option if none selected
                            if (checked && newPermissions.collectionCasesView === 'none') {
                              newPermissions.collectionCasesView = 'assigned_default';
                            }
                            
                            setFormData(prev => ({ ...prev, permissions: newPermissions }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="cc-delete" className="text-sm font-medium leading-none cursor-pointer">
                            Delete collection cases
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can delete collection cases
                      </p>
                    </div>

                    {/* Reassign User Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Reassign User</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="cc-reassign"
                          checked={formData.permissions.collectionCasesReassign}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                collectionCasesReassign: checked === true,
                              }
                            }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="cc-reassign" className="text-sm font-medium leading-none cursor-pointer">
                            Reassign collection cases to other users
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can change collection case ownership
                      </p>
                    </div>
                        </div>
                      </div>

                      {/* Promise to Pay Column */}
                      <div className="space-y-6">
                        <div className="flex items-center gap-3 pb-4 border-b">
                          <div className="p-2 bg-green-500/10 rounded-lg">
                            <HandshakeIcon className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">Promise to Pay</h3>
                            <p className="text-sm text-muted-foreground">Manage payment promise permissions</p>
                          </div>
                        </div>
                        
                        <div className="space-y-6">
                    {/* View Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">View</Label>
                      </div>
                      <RadioGroup
                        value={formData.permissions.promiseToPayView}
                        onValueChange={(value: string) => {
                          if (formData.isSystem) return;
                          const v = value as PermissionViewLevel;
                          const newPermissions = { ...formData.permissions, promiseToPayView: v };
                          
                          // If setting to 'none', clear all dependent permissions
                          if (value === 'none') {
                            newPermissions.promiseToPayCreate = false;
                            newPermissions.promiseToPayEdit = 'none';
                            newPermissions.promiseToPayDelete = false;
                            newPermissions.promiseToPayReassign = false;
                          }
                          
                          setFormData(prev => ({ ...prev, permissions: newPermissions }));
                        }}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="all" id="ptp-view-all" disabled={formData.isSystem} />
                          <Label htmlFor="ptp-view-all" className="font-normal cursor-pointer">
                            View All
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned_default" id="ptp-view-assigned-default" disabled={formData.isSystem} />
                          <Label htmlFor="ptp-view-assigned-default" className="font-normal cursor-pointer">
                            View Assigned by default
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned_only" id="ptp-view-assigned-only" disabled={formData.isSystem} />
                          <Label htmlFor="ptp-view-assigned-only" className="font-normal cursor-pointer">
                            View Assigned Only
                          </Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-muted-foreground">
                        Controls which promises to pay users can access
                      </p>
                    </div>

                    {/* Create Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Create</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="ptp-create"
                          checked={formData.permissions.promiseToPayCreate}
                          onCheckedChange={(checked) => {
                            const newPermissions = { ...formData.permissions, promiseToPayCreate: checked === true };
                            
                            // When Create is checked, automatically select "View Assigned by default" if no view is selected
                            if (checked && newPermissions.promiseToPayView === 'none') {
                              newPermissions.promiseToPayView = 'assigned_default';
                            }
                            
                            setFormData(prev => ({ ...prev, permissions: newPermissions }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="ptp-create" className="text-sm font-medium leading-none cursor-pointer">
                            Create promises to pay
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can create new payment promises
                      </p>
                    </div>

                    {/* Edit Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Edit</Label>
                      </div>
                      <RadioGroup
                        value={formData.permissions.promiseToPayEdit}
                        onValueChange={(value: string) => {
                          if (formData.isSystem) return;
                          const v = value as PermissionEditLevel;
                          const newPermissions = { ...formData.permissions, promiseToPayEdit: v };
                          
                          // When Edit is selected, automatically enable a View option if none selected
                          if (value !== 'none' && newPermissions.promiseToPayView === 'none') {
                            newPermissions.promiseToPayView = 'assigned_default';
                          }
                          
                          setFormData(prev => ({ ...prev, permissions: newPermissions }));
                        }}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="all" id="ptp-edit-all" disabled={formData.isSystem} />
                          <Label htmlFor="ptp-edit-all" className="font-normal cursor-pointer">
                            Edit All
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="assigned" id="ptp-edit-assigned" disabled={formData.isSystem} />
                          <Label htmlFor="ptp-edit-assigned" className="font-normal cursor-pointer">
                            Edit Assigned
                          </Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-muted-foreground">
                        Controls which promises to pay users can modify
                      </p>
                    </div>

                    {/* Delete Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Delete</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="ptp-delete"
                          checked={formData.permissions.promiseToPayDelete}
                          onCheckedChange={(checked) => {
                            const newPermissions = { ...formData.permissions, promiseToPayDelete: checked === true };
                            
                            // When Delete is checked, automatically enable a View option if none selected
                            if (checked && newPermissions.promiseToPayView === 'none') {
                              newPermissions.promiseToPayView = 'assigned_default';
                            }
                            
                            setFormData(prev => ({ ...prev, permissions: newPermissions }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="ptp-delete" className="text-sm font-medium leading-none cursor-pointer">
                            Delete promises to pay
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can delete payment promises
                      </p>
                    </div>

                    {/* Reassign User Permission */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Reassign User</Label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="ptp-reassign"
                          checked={formData.permissions.promiseToPayReassign}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                promiseToPayReassign: checked === true,
                              }
                            }));
                          }}
                          disabled={formData.isSystem}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="ptp-reassign" className="text-sm font-medium leading-none cursor-pointer">
                            Reassign promises to pay to other users
                          </Label>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Can change promise to pay ownership
                      </p>
                    </div>
                        </div>
                      </div>
                    </div>

                    {/* Info banner for system roles */}
                    {formData.isSystem && (
                      <div className="mt-6 pt-6 border-t">
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <p>
                            This is a system role. Permissions cannot be modified directly. 
                            Create a custom role to define specific permissions.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="dispute-management" className="mt-0">
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold mb-2">Dispute Management Permissions</h3>
                <p className="text-muted-foreground">This tab will contain detailed dispute management permissions</p>
              </div>
            </TabsContent>

            <TabsContent value="credit-scoring" className="mt-0">
              <div className="max-w-4xl mx-auto">
                <Card>
                  <CardContent className="py-16">
                    <div className="text-center space-y-4">
                      <div className="flex justify-center">
                        <div className="p-4 bg-muted rounded-full">
                          <TrendingUp className="h-12 w-12 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-semibold">Credit Scoring</h3>
                        <p className="text-muted-foreground max-w-md mx-auto">
                          Credit scoring permissions will be available here once the credit scoring feature is enabled for your organization.
                        </p>
                      </div>
                      <div className="pt-4">
                        <Badge variant="secondary" className="text-sm">
                          Coming Soon
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="analytics-reporting" className="mt-0">
              <div className="max-w-4xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle>Analytics & Reporting Permissions</CardTitle>
                        <CardDescription>
                          Configure dashboard access and manager-level functionality
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    {/* Dashboards Section */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-1">Dashboards</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to specific dashboard views
                        </p>
                      </div>

                      <div className="space-y-4">
                        {/* View Credit Control Dashboard */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="view-credit-control-dashboard"
                            checked={formData.permissions.viewCreditControlDashboard}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  viewCreditControlDashboard: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="view-credit-control-dashboard" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              View Credit Control Dashboard
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Access to main credit control overview and metrics
                            </p>
                          </div>
                        </div>

                        {/* View Collections Dashboard */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="view-collections-dashboard"
                            checked={formData.permissions.viewCollectionsDashboard}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  viewCollectionsDashboard: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="view-collections-dashboard" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              View Collections Dashboard
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Access to collections queue and team performance metrics
                            </p>
                          </div>
                        </div>

                        {/* View Disputes Dashboard */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="view-disputes-dashboard"
                            checked={formData.permissions.viewDisputesDashboard}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  viewDisputesDashboard: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="view-disputes-dashboard" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              View Disputes Dashboard
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Access to dispute tracking and analytics
                            </p>
                          </div>
                        </div>

                        {/* View Ageing Analysis */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="view-ageing-analysis"
                            checked={formData.permissions.viewAgeingAnalysis}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  viewAgeingAnalysis: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="view-ageing-analysis" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              View Ageing Analysis
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Access to accounts receivable ageing reports
                            </p>
                          </div>
                        </div>

                        {/* View Multi-Company Dashboard */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="view-multi-company-dashboard"
                            checked={formData.permissions.viewMultiCompanyDashboard}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  viewMultiCompanyDashboard: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="view-multi-company-dashboard" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              View Multi-Company Dashboard
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Access to consolidated multi-company view
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t" />

                    {/* Manager Access Section */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-1">Manager Access</h3>
                        <p className="text-sm text-muted-foreground">
                          Enable manager-level functionality across dashboards
                        </p>
                      </div>

                      <div className="space-y-4">
                        {/* Access Manager Views */}
                        <div className="flex items-start gap-4">
                          <Checkbox
                            id="access-manager-views"
                            checked={formData.permissions.accessManagerViews}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                permissions: {
                                  ...prev.permissions,
                                  accessManagerViews: checked === true,
                                }
                              }));
                            }}
                            disabled={formData.isSystem}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <Label 
                              htmlFor="access-manager-views" 
                              className="text-sm font-semibold cursor-pointer"
                            >
                              Access Manager Views
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Can switch between user views and access manager-level functionality across dashboards
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info banner for system roles */}
                    {formData.isSystem && (
                      <div className="mt-6 pt-6 border-t">
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <p>
                            This is a system role. Permissions cannot be modified directly. 
                            Create a custom role to define specific permissions.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="administration" className="mt-0">
              <div className="max-w-4xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Settings className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle>Administration Permissions</CardTitle>
                        <CardDescription>
                          Configure access to system administration and configuration
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    {/* Manage Users Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Users</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to user account management
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-users-view"
                            checked={formData.permissions.adminManageUsersView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageUsersView = checked === true;
                              
                              // When unchecking View, uncheck all dependent permissions
                              if (!checked) {
                                newPermissions.adminManageUsersCreate = false;
                                newPermissions.adminManageUsersEdit = false;
                                newPermissions.adminManageUsersDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-users-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-users-create"
                            checked={formData.permissions.adminManageUsersCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageUsersCreate = checked === true;
                              
                              // When checking Create, automatically check View
                              if (checked) {
                                newPermissions.adminManageUsersView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-users-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-users-edit"
                            checked={formData.permissions.adminManageUsersEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageUsersEdit = checked === true;
                              
                              // When checking Edit, automatically check View
                              if (checked) {
                                newPermissions.adminManageUsersView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-users-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-users-delete"
                            checked={formData.permissions.adminManageUsersDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageUsersDelete = checked === true;
                              
                              // When checking Delete, automatically check View
                              if (checked) {
                                newPermissions.adminManageUsersView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-users-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage Companies Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Companies</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to company settings and configurations
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-companies-view"
                            checked={formData.permissions.adminManageCompaniesView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCompaniesView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageCompaniesCreate = false;
                                newPermissions.adminManageCompaniesEdit = false;
                                newPermissions.adminManageCompaniesDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-companies-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-companies-create"
                            checked={formData.permissions.adminManageCompaniesCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCompaniesCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCompaniesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-companies-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-companies-edit"
                            checked={formData.permissions.adminManageCompaniesEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCompaniesEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCompaniesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-companies-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-companies-delete"
                            checked={formData.permissions.adminManageCompaniesDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCompaniesDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCompaniesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-companies-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage General Settings Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage General Settings</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to general system settings
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-general-view"
                            checked={formData.permissions.adminManageGeneralSettingsView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageGeneralSettingsView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageGeneralSettingsCreate = false;
                                newPermissions.adminManageGeneralSettingsEdit = false;
                                newPermissions.adminManageGeneralSettingsDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-general-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-general-create"
                            checked={formData.permissions.adminManageGeneralSettingsCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageGeneralSettingsCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageGeneralSettingsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-general-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-general-edit"
                            checked={formData.permissions.adminManageGeneralSettingsEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageGeneralSettingsEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageGeneralSettingsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-general-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-general-delete"
                            checked={formData.permissions.adminManageGeneralSettingsDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageGeneralSettingsDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageGeneralSettingsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-general-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage Integrations Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Integrations</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to system integrations and connections
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-integrations-view"
                            checked={formData.permissions.adminManageIntegrationsView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageIntegrationsView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageIntegrationsCreate = false;
                                newPermissions.adminManageIntegrationsEdit = false;
                                newPermissions.adminManageIntegrationsDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-integrations-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-integrations-create"
                            checked={formData.permissions.adminManageIntegrationsCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageIntegrationsCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageIntegrationsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-integrations-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-integrations-edit"
                            checked={formData.permissions.adminManageIntegrationsEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageIntegrationsEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageIntegrationsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-integrations-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-integrations-delete"
                            checked={formData.permissions.adminManageIntegrationsDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageIntegrationsDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageIntegrationsView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-integrations-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage Templates Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Templates</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to email and communication templates
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-templates-view"
                            checked={formData.permissions.adminManageTemplatesView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageTemplatesView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageTemplatesCreate = false;
                                newPermissions.adminManageTemplatesEdit = false;
                                newPermissions.adminManageTemplatesDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-templates-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-templates-create"
                            checked={formData.permissions.adminManageTemplatesCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageTemplatesCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageTemplatesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-templates-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-templates-edit"
                            checked={formData.permissions.adminManageTemplatesEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageTemplatesEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageTemplatesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-templates-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-templates-delete"
                            checked={formData.permissions.adminManageTemplatesDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageTemplatesDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageTemplatesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-templates-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage Collection Strategies Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Collection Strategies</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to collection strategy configuration
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-strategies-view"
                            checked={formData.permissions.adminManageCollectionStrategiesView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCollectionStrategiesView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageCollectionStrategiesCreate = false;
                                newPermissions.adminManageCollectionStrategiesEdit = false;
                                newPermissions.adminManageCollectionStrategiesDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-strategies-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-strategies-create"
                            checked={formData.permissions.adminManageCollectionStrategiesCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCollectionStrategiesCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCollectionStrategiesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-strategies-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-strategies-edit"
                            checked={formData.permissions.adminManageCollectionStrategiesEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCollectionStrategiesEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCollectionStrategiesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-strategies-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-strategies-delete"
                            checked={formData.permissions.adminManageCollectionStrategiesDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageCollectionStrategiesDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageCollectionStrategiesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-strategies-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Manage Dispute Types Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Manage Dispute Types</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to dispute type definitions
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-disputes-view"
                            checked={formData.permissions.adminManageDisputeTypesView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageDisputeTypesView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminManageDisputeTypesCreate = false;
                                newPermissions.adminManageDisputeTypesEdit = false;
                                newPermissions.adminManageDisputeTypesDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-disputes-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-disputes-create"
                            checked={formData.permissions.adminManageDisputeTypesCreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageDisputeTypesCreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageDisputeTypesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-disputes-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-disputes-edit"
                            checked={formData.permissions.adminManageDisputeTypesEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageDisputeTypesEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageDisputeTypesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-disputes-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-disputes-delete"
                            checked={formData.permissions.adminManageDisputeTypesDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminManageDisputeTypesDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminManageDisputeTypesView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-disputes-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Configure AI Section */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-base font-semibold mb-1">Configure AI</h3>
                        <p className="text-sm text-muted-foreground">
                          Control access to AI and machine learning settings
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-ai-view"
                            checked={formData.permissions.adminConfigureAIView}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminConfigureAIView = checked === true;
                              
                              if (!checked) {
                                newPermissions.adminConfigureAICreate = false;
                                newPermissions.adminConfigureAIEdit = false;
                                newPermissions.adminConfigureAIDelete = false;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-ai-view" className="text-sm font-medium cursor-pointer">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-ai-create"
                            checked={formData.permissions.adminConfigureAICreate}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminConfigureAICreate = checked === true;
                              
                              if (checked) {
                                newPermissions.adminConfigureAIView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-ai-create" className="text-sm font-medium cursor-pointer">
                            Create
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-ai-edit"
                            checked={formData.permissions.adminConfigureAIEdit}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminConfigureAIEdit = checked === true;
                              
                              if (checked) {
                                newPermissions.adminConfigureAIView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-ai-edit" className="text-sm font-medium cursor-pointer">
                            Edit
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="admin-ai-delete"
                            checked={formData.permissions.adminConfigureAIDelete}
                            onCheckedChange={(checked) => {
                              const newPermissions = { ...formData.permissions };
                              newPermissions.adminConfigureAIDelete = checked === true;
                              
                              if (checked) {
                                newPermissions.adminConfigureAIView = true;
                              }
                              
                              setFormData(prev => ({ ...prev, permissions: newPermissions }));
                            }}
                            disabled={formData.isSystem}
                          />
                          <Label htmlFor="admin-ai-delete" className="text-sm font-medium cursor-pointer">
                            Delete
                          </Label>
                        </div>
                      </div>
                    </div>

                    {/* Info banner for system roles */}
                    {formData.isSystem && (
                      <div className="mt-6 pt-6 border-t">
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <p>
                            This is a system role. Permissions cannot be modified directly. 
                            Create a custom role to define specific permissions.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="users" className="mt-0">
              <div className="max-w-6xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <UsersIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle>Assigned Users</CardTitle>
                        <CardDescription>
                          Users currently assigned to this role
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {ASSIGNED_USERS_BY_ROLE[roleId || '']?.length > 0 ? (
                      <>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[250px]">User Name</TableHead>
                                <TableHead className="w-[300px]">Email</TableHead>
                                <TableHead className="w-[120px]">Status</TableHead>
                                <TableHead className="w-[150px]">Date Assigned</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {ASSIGNED_USERS_BY_ROLE[roleId || ''].map((user) => (
                                <TableRow key={user.id}>
                                  <TableCell>
                                    <UserAvatar
                                      user={{
                                        firstName: user.firstName,
                                        lastName: user.lastName,
                                        avatar: user.avatar,
                                      }}
                                      size="sm"
                                      showName
                                    />
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {user.email}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant={user.status === 'active' ? 'default' : 'secondary'}
                                      className={
                                        user.status === 'active'
                                          ? 'bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20'
                                          : 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
                                      }
                                    >
                                      {user.status === 'active' ? 'Active' : 'Inactive'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {format(new Date(user.dateAssigned), 'dd MMM yyyy')}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        
                        {/* Info note below table */}
                        <div className="mt-6 pt-4 border-t">
                          <div className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <p>
                              To add or remove users from this role, go to User Management
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="mb-4 p-4 bg-muted rounded-full">
                          <UsersIcon className="h-12 w-12 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No users assigned to this role</h3>
                        <p className="text-sm text-muted-foreground max-w-md">
                          Users can be assigned to this role from the User Management page
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <div className="max-w-7xl mx-auto">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <History className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle>Audit History Log</CardTitle>
                          <CardDescription>
                            Comprehensive audit trail of all changes made to this role
                          </CardDescription>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={handleExportAuditLog}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Export Audit Log
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Filter Controls */}
                    <div className="flex flex-wrap items-end gap-4 p-4 bg-muted/50 rounded-lg border">
                      {/* Date Range Filter */}
                      <div className="flex-1 min-w-[200px] space-y-2">
                        <Label className="text-sm font-medium">Date Range</Label>
                        <div className="flex gap-2">
                          <Button
                            variant={auditDateRange === '7days' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setAuditDateRange('7days');
                              setAuditCurrentPage(1);
                            }}
                            className="flex-1"
                          >
                            Last 7 days
                          </Button>
                          <Button
                            variant={auditDateRange === '30days' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setAuditDateRange('30days');
                              setAuditCurrentPage(1);
                            }}
                            className="flex-1"
                          >
                            Last 30 days
                          </Button>
                          <Button
                            variant={auditDateRange === '90days' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setAuditDateRange('90days');
                              setAuditCurrentPage(1);
                            }}
                            className="flex-1"
                          >
                            Last 90 days
                          </Button>
                          <Button
                            variant={auditDateRange === 'all' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setAuditDateRange('all');
                              setAuditCurrentPage(1);
                            }}
                            className="flex-1"
                          >
                            All time
                          </Button>
                        </div>
                      </div>

                      {/* User Filter */}
                      <div className="w-[200px] space-y-2">
                        <Label className="text-sm font-medium">User</Label>
                        <Select
                          value={auditUserFilter}
                          onValueChange={(value) => {
                            setAuditUserFilter(value);
                            setAuditCurrentPage(1);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Users" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Users</SelectItem>
                            {uniqueAuditUsers.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Action Type Filter */}
                      <div className="w-[220px] space-y-2">
                        <Label className="text-sm font-medium">Action Type</Label>
                        <Select
                          value={auditActionFilter}
                          onValueChange={(value) => {
                            setAuditActionFilter(value);
                            setAuditCurrentPage(1);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Actions" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Actions</SelectItem>
                            <SelectItem value="permission_added">Permission Added</SelectItem>
                            <SelectItem value="permission_removed">Permission Removed</SelectItem>
                            <SelectItem value="role_details_updated">Role Details Updated</SelectItem>
                            <SelectItem value="role_created">Role Created</SelectItem>
                            <SelectItem value="role_duplicated">Role Duplicated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Audit Log Table */}
                    {filteredAuditLogs.length > 0 ? (
                      <>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50">
                                <TableHead className="w-[180px]">Date/Time</TableHead>
                                <TableHead className="w-[200px]">User</TableHead>
                                <TableHead className="w-[180px]">Action Type</TableHead>
                                <TableHead>Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedAuditLogs.map((log, index) => (
                                <TableRow 
                                  key={log.id}
                                  className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                                >
                                  <TableCell className="font-medium text-sm">
                                    {format(new Date(log.dateTime), 'dd MMM yyyy, HH:mm')}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <UserAvatar
                                        user={{
                                          firstName: log.userName.split(' ')[0],
                                          lastName: log.userName.split(' ')[1] || '',
                                          avatar: log.userAvatar,
                                        }}
                                        size="sm"
                                      />
                                      <span className="text-sm font-medium">{log.userName}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="secondary"
                                      className={getActionTypeBadgeColor(log.actionType)}
                                    >
                                      {getActionTypeLabel(log.actionType)}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {log.details}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Pagination */}
                        {totalAuditPages > 1 && (
                          <div className="flex items-center justify-between pt-4">
                            <div className="text-sm text-muted-foreground">
                              Showing {(auditCurrentPage - 1) * AUDIT_ITEMS_PER_PAGE + 1}-
                              {Math.min(auditCurrentPage * AUDIT_ITEMS_PER_PAGE, filteredAuditLogs.length)} of{' '}
                              {filteredAuditLogs.length} entries
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setAuditCurrentPage((prev) => Math.max(1, prev - 1))}
                                disabled={auditCurrentPage === 1}
                                className="gap-1"
                              >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                              </Button>
                              <div className="flex items-center gap-1">
                                {Array.from({ length: totalAuditPages }, (_, i) => i + 1)
                                  .filter(page => {
                                    // Show first page, last page, current page, and pages around current
                                    return (
                                      page === 1 ||
                                      page === totalAuditPages ||
                                      Math.abs(page - auditCurrentPage) <= 1
                                    );
                                  })
                                  .map((page, index, array) => {
                                    // Add ellipsis if there's a gap
                                    const prevPage = array[index - 1];
                                    const showEllipsis = prevPage && page - prevPage > 1;
                                    
                                    return (
                                      <div key={page} className="flex items-center gap-1">
                                        {showEllipsis && (
                                          <span className="px-2 text-muted-foreground">...</span>
                                        )}
                                        <Button
                                          variant={auditCurrentPage === page ? 'default' : 'outline'}
                                          size="sm"
                                          onClick={() => setAuditCurrentPage(page)}
                                          className="w-8 h-8 p-0"
                                        >
                                          {page}
                                        </Button>
                                      </div>
                                    );
                                  })}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setAuditCurrentPage((prev) => Math.min(totalAuditPages, prev + 1))}
                                disabled={auditCurrentPage === totalAuditPages}
                                className="gap-1"
                              >
                                Next
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Empty State */
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="mb-4 p-4 bg-muted rounded-full">
                          <History className="h-12 w-12 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No audit entries found</h3>
                        <p className="text-sm text-muted-foreground max-w-md">
                          Try adjusting your filters to see more results
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </div>
          </Tabs>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation} className="bg-destructive hover:bg-destructive/90">
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
